import {
  MouseEvent as ReactMouseEvent,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

const MIN_ZOOM = 0.3;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.15;
const PAN_THRESHOLD = 5;

const getTouchDistance = (t1: Touch, t2: Touch): number => {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

const getTouchMidpoint = (t1: Touch, t2: Touch): { x: number; y: number } => ({
  x: (t1.clientX + t2.clientX) / 2,
  y: (t1.clientY + t2.clientY) / 2
});

export interface TableBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  contentWidth: number;
  contentHeight: number;
}

interface UsePanZoomOptions {
  /** Bounding rectangle of all tables – used to compute initial fit zoom/pan. */
  tableBounds: TableBounds | undefined;
  /** Changing this value resets zoom & pan to fit the new layout. */
  selectedFloorplan: unknown;
  /** Called when the user clicks empty space or presses Escape. */
  onContainerClick: () => void;
  /** While true the container element has not yet mounted. */
  loading: boolean;
  /** Number of renderable tables – used to re-attach listeners once tables appear. */
  tablesCount: number;
  /** Initial zoom hint stored in the venue config (usually 1). */
  initialZoomLevel?: number;
}

export interface UsePanZoomReturn {
  /** Attach this ref to the container `<div>`. */
  containerRef: RefObject<HTMLDivElement>;
  zoomLevel: number;
  panOffset: { x: number; y: number };
  isPanning: boolean;
  handleZoomIn: (e: ReactMouseEvent<HTMLButtonElement>) => void;
  handleZoomOut: (e: ReactMouseEvent<HTMLButtonElement>) => void;
  handleResetZoom: (e: ReactMouseEvent<HTMLButtonElement>) => void;
}

export const usePanZoom = ({
  tableBounds,
  selectedFloorplan,
  onContainerClick,
  loading,
  tablesCount,
  initialZoomLevel = 1
}: UsePanZoomOptions): UsePanZoomReturn => {
  // ── State ──────────────────────────────────────────────────────────────
  const [zoomLevel, setZoomLevel] = useState<number>(initialZoomLevel);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0
  });
  const [isPanning, setIsPanning] = useState(false);
  const [defaultZoom, setDefaultZoom] = useState<number>(1);

  // ── Refs ───────────────────────────────────────────────────────────────
  const containerRef = useRef<HTMLDivElement>(null);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const panOffsetStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const isPointerDownRef = useRef(false);
  const isPanningRef = useRef(false);
  const hasPannedRef = useRef(false);
  const panOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const zoomLevelRef = useRef<number>(initialZoomLevel);

  // Pinch-to-zoom refs
  const isPinchingRef = useRef(false);
  const initialPinchDistanceRef = useRef<number>(0);
  const initialPinchZoomRef = useRef<number>(1);
  const initialPinchPanRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const initialPinchMidpointRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0
  });
  const pinchContainerRectRef = useRef<{ left: number; top: number }>({
    left: 0,
    top: 0
  });

  // Tracks which floorplan has been auto-fitted so we only fit once per
  // floorplan change (prevents re-fitting when bookings update).
  const lastFittedFloorplanRef = useRef<unknown>(null);

  // Keep refs in sync with state so event handlers always read fresh values.
  useEffect(() => {
    panOffsetRef.current = panOffset;
  }, [panOffset]);

  useEffect(() => {
    isPanningRef.current = isPanning;
  }, [isPanning]);

  useEffect(() => {
    zoomLevelRef.current = zoomLevel;
  }, [zoomLevel]);

  // ── Auto-fit zoom & pan when the floorplan / bounds change ─────────────
  // `loading` is included so the effect re-runs once the container mounts
  // (it only renders after loading finishes). The ref guard ensures we only
  // fit once per floorplan change, preventing resets when bookings update.
  useEffect(() => {
    if (!tableBounds || !containerRef.current) return;

    // Only re-fit when the selected floorplan actually changes.
    if (lastFittedFloorplanRef.current === selectedFloorplan) return;
    lastFittedFloorplanRef.current = selectedFloorplan;

    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    if (containerWidth === 0 || containerHeight === 0) return;

    const { minX, minY, contentWidth, contentHeight } = tableBounds;

    const padding = 40;
    const fitZoom = Math.min(
      (containerWidth - padding * 2) / contentWidth,
      (containerHeight - padding * 2) / contentHeight,
      MAX_ZOOM
    );
    const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));

    setDefaultZoom(clampedZoom);
    setZoomLevel(clampedZoom);
    const offsetX =
      (containerWidth - contentWidth * clampedZoom) / 2 - minX * clampedZoom;
    const offsetY =
      (containerHeight - contentHeight * clampedZoom) / 2 - minY * clampedZoom;

    setPanOffset({ x: offsetX, y: offsetY });
  }, [selectedFloorplan, tableBounds, loading]);

  // ── Mouse handlers ────────────────────────────────────────────────────
  const handleMouseDown = useCallback((e: MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-floating-badge]")) return;
    if ((e.target as HTMLElement).closest("[data-legend]")) return;
    isPointerDownRef.current = true;
    hasPannedRef.current = false;
    panStartRef.current = { x: e.clientX, y: e.clientY };
    panOffsetStartRef.current = { ...panOffsetRef.current };
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isPointerDownRef.current) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    if (
      !isPanningRef.current &&
      Math.sqrt(dx * dx + dy * dy) >= PAN_THRESHOLD
    ) {
      isPanningRef.current = true;
      setIsPanning(true);
      hasPannedRef.current = true;
    }
    if (isPanningRef.current) {
      setPanOffset({
        x: panOffsetStartRef.current.x + dx,
        y: panOffsetStartRef.current.y + dy
      });
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isPointerDownRef.current = false;
    isPanningRef.current = false;
    setIsPanning(false);
  }, []);

  // ── Wheel (zoom) ──────────────────────────────────────────────────────
  const handleWheel = useCallback((e: WheelEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    const oldZoom = zoomLevelRef.current;
    const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, oldZoom + delta));
    if (newZoom === oldZoom) return;

    // Anchor zoom to the mouse pointer position so the content under the
    // cursor stays fixed while the rest scales around it.
    const rect = containerRef.current?.getBoundingClientRect();
    const mouseX = e.clientX - (rect?.left ?? 0);
    const mouseY = e.clientY - (rect?.top ?? 0);
    const pan = panOffsetRef.current;

    const contentX = (mouseX - pan.x) / oldZoom;
    const contentY = (mouseY - pan.y) / oldZoom;

    setZoomLevel(newZoom);
    setPanOffset({
      x: mouseX - contentX * newZoom,
      y: mouseY - contentY * newZoom
    });
  }, []);

  // ── Touch handlers (single-finger pan + two-finger pinch zoom) ────────
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2) {
      // Begin pinch-to-zoom
      e.preventDefault();
      isPinchingRef.current = true;
      isPointerDownRef.current = false;
      isPanningRef.current = false;
      setIsPanning(false);
      initialPinchDistanceRef.current = getTouchDistance(
        e.touches[0],
        e.touches[1]
      );
      initialPinchZoomRef.current = zoomLevelRef.current;
      initialPinchPanRef.current = { ...panOffsetRef.current };
      // Capture the container rect at pinch start so we can convert client
      // coordinates to container-local space consistently throughout the gesture.
      const rect = containerRef.current?.getBoundingClientRect();
      pinchContainerRectRef.current = {
        left: rect?.left ?? 0,
        top: rect?.top ?? 0
      };
      const mid = getTouchMidpoint(e.touches[0], e.touches[1]);
      initialPinchMidpointRef.current = {
        x: mid.x - pinchContainerRectRef.current.left,
        y: mid.y - pinchContainerRectRef.current.top
      };
    } else if (e.touches.length === 1 && !isPinchingRef.current) {
      const touch = e.touches[0];
      isPointerDownRef.current = true;
      hasPannedRef.current = false;
      panStartRef.current = { x: touch.clientX, y: touch.clientY };
      panOffsetStartRef.current = { ...panOffsetRef.current };
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    // Two-finger pinch zoom
    if (isPinchingRef.current && e.touches.length === 2) {
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / initialPinchDistanceRef.current;
      const newZoom = Math.min(
        MAX_ZOOM,
        Math.max(MIN_ZOOM, initialPinchZoomRef.current * scale)
      );

      // Convert current midpoint to container-local space using the rect
      // captured at pinch start (the container doesn't move during a gesture).
      const rawMid = getTouchMidpoint(e.touches[0], e.touches[1]);
      const mid = {
        x: rawMid.x - pinchContainerRectRef.current.left,
        y: rawMid.y - pinchContainerRectRef.current.top
      };

      // Anchor zoom to the midpoint between the two fingers.
      // initialPinchMidpointRef is already in container-local space.
      const oldZoom = initialPinchZoomRef.current;
      const contentX =
        (initialPinchMidpointRef.current.x - initialPinchPanRef.current.x) /
        oldZoom;
      const contentY =
        (initialPinchMidpointRef.current.y - initialPinchPanRef.current.y) /
        oldZoom;

      const panX = mid.x - contentX * newZoom;
      const panY = mid.y - contentY * newZoom;

      setZoomLevel(newZoom);
      setPanOffset({ x: panX, y: panY });
      return;
    }

    // Single-finger pan
    if (!isPointerDownRef.current || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const dx = touch.clientX - panStartRef.current.x;
    const dy = touch.clientY - panStartRef.current.y;
    if (
      !isPanningRef.current &&
      Math.sqrt(dx * dx + dy * dy) >= PAN_THRESHOLD
    ) {
      isPanningRef.current = true;
      setIsPanning(true);
      hasPannedRef.current = true;
    }
    if (isPanningRef.current) {
      setPanOffset({
        x: panOffsetStartRef.current.x + dx,
        y: panOffsetStartRef.current.y + dy
      });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (isPinchingRef.current) {
      isPinchingRef.current = false;
      return;
    }
    isPointerDownRef.current = false;
    isPanningRef.current = false;
    setIsPanning(false);
  }, []);

  // ── Click (deselect / ignore after pan) ───────────────────────────────
  const handleContainerClickEvent = useCallback(
    (e: MouseEvent) => {
      if (hasPannedRef.current) {
        hasPannedRef.current = false;
        return;
      }
      if (!(e.target as HTMLElement).closest("[data-table-id]")) {
        onContainerClick();
      }
    },
    [onContainerClick]
  );

  // ── Keyboard (arrows → pan, +/- → zoom, Escape → deselect) ───────────
  const handleContainerKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const PAN_STEP = 20;
      switch (e.key) {
        case "Escape":
          onContainerClick();
          break;
        case "ArrowUp":
          e.preventDefault();
          setPanOffset((prev) => ({ ...prev, y: prev.y + PAN_STEP }));
          break;
        case "ArrowDown":
          e.preventDefault();
          setPanOffset((prev) => ({ ...prev, y: prev.y - PAN_STEP }));
          break;
        case "ArrowLeft":
          e.preventDefault();
          setPanOffset((prev) => ({ ...prev, x: prev.x + PAN_STEP }));
          break;
        case "ArrowRight":
          e.preventDefault();
          setPanOffset((prev) => ({ ...prev, x: prev.x - PAN_STEP }));
          break;
        case "+":
        case "=":
          e.preventDefault();
          setZoomLevel((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
          break;
        case "-":
          e.preventDefault();
          setZoomLevel((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
          break;
      }
    },
    [onContainerClick]
  );

  // ── Attach / detach DOM event listeners ───────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.tabIndex = 0;

    el.addEventListener("mousedown", handleMouseDown);
    el.addEventListener("mousemove", handleMouseMove);
    el.addEventListener("mouseup", handleMouseUp);
    el.addEventListener("mouseleave", handleMouseUp);
    el.addEventListener("wheel", handleWheel, { passive: false });
    el.addEventListener("touchstart", handleTouchStart, { passive: false });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);
    el.addEventListener("click", handleContainerClickEvent);
    el.addEventListener("keydown", handleContainerKeyDown);

    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      el.removeEventListener("mousemove", handleMouseMove);
      el.removeEventListener("mouseup", handleMouseUp);
      el.removeEventListener("mouseleave", handleMouseUp);
      el.removeEventListener("wheel", handleWheel);
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("click", handleContainerClickEvent);
      el.removeEventListener("keydown", handleContainerKeyDown);
    };
  }, [
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleContainerClickEvent,
    handleContainerKeyDown,
    // The <section> with containerRef only mounts once loading is false and a
    // floorplan is selected. Including these ensures the effect re-runs (and
    // attaches listeners) when the DOM element first appears.
    loading,
    selectedFloorplan,
    tablesCount
  ]);

  // ── Button handlers exposed to the UI ─────────────────────────────────
  const handleZoomOut = useCallback((e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setZoomLevel((prev) => Math.max(MIN_ZOOM, prev - ZOOM_STEP));
  }, []);

  const handleZoomIn = useCallback((e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setZoomLevel((prev) => Math.min(MAX_ZOOM, prev + ZOOM_STEP));
  }, []);

  const handleResetZoom = useCallback(
    (e: ReactMouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setZoomLevel(defaultZoom);
      const container = containerRef.current;
      if (container && tableBounds) {
        setPanOffset({
          x:
            (container.clientWidth - tableBounds.contentWidth * defaultZoom) /
              2 -
            tableBounds.minX * defaultZoom,
          y:
            (container.clientHeight - tableBounds.contentHeight * defaultZoom) /
              2 -
            tableBounds.minY * defaultZoom
        });
      }
    },
    [defaultZoom, tableBounds]
  );

  return {
    containerRef,
    zoomLevel,
    panOffset,
    isPanning,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom
  };
};
